import React, { useEffect, useState } from 'react';
import { useOwned } from './useCrm';
import {
  watchPath, logActivity, updateActivity, callFields, updateContactChecked, noCalls, optedOutCalls, optedOutTexts,
  formatPhoneDash, phoneE164, DISPOSITIONS, recordAskedNotToCall, isNotInterested, pauseInfo,
  visibleTemplates, markAttemptedContact, } from '../../services/crmService';
import { applyMerge } from './merge';
import { getUserContact } from '../../services/userService';
import ReengageModal from './ReengageModal';
import CallbackModal from './CallbackModal';
import AppointmentModal from './AppointmentModal';
import LogAppointmentModal from '../../components/outreach/LogAppointmentModal';
import { NOTIFICATIONS_ENABLED } from '../../config/notifications';
import { ui, MUTED, DANGER } from './crmUi';
import { auth } from '../../firebaseConfig';
import { canMessageLead } from '../../shared/leadPermissions';
import { clearTasksForLoggedContact } from '../../services/callTaskClear';

// Call / Text / Email shortcuts on a contact. Twilio and SendGrid aren't connected yet, so Call and Text
// open the phone's own app (tel:/sms:) and Email opens the user's mail app (mailto:). Every action is
// logged on the contact's timeline and stamps lastContactedAt.
const plainFromBlocks = (blocks = []) => blocks
  .map((b) => (b.type === 'heading' || b.type === 'text' ? b.text : b.type === 'button' && b.url ? `${b.text}: ${b.url}` : b.type === 'listing' ? [b.address, b.price, b.url].filter(Boolean).join(' – ') : ''))
  .filter(Boolean).join('\n\n');

const overlay = { position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 };
const modal = { background: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box' };

// Send via Quo is Kelly's business number, so only her account sees it (the server also enforces QUO_ALLOWED_EMAILS).
const QUO_UI_EMAILS = ['kelly.olin@exprealty.com'];

export default function QuickActions({ contact, uid, isAdmin, senderName, compact = false, task = null, onTaskCleared = null }) {
  // Follow-up task script (12 Day Plan), merged with this lead and the signed-in agent's profile.
  const [me, setMe] = useState({});
  useEffect(() => { if (task && task.script && uid) getUserContact(uid).then((x) => setMe(x || {})).catch(() => {}); }, [task, uid]);
  const script = task && task.script ? applyMerge(task.script, contact, me) : '';
  const [mode, setMode] = useState(null); // 'call' | 'text' | 'email'
  const [result, setResult] = useState('');
  const [note, setNote] = useState('');
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState('');
  const own_Templates = useOwned('crm/templates', { scoped: true });
  useEffect(() => setTemplates(own_Templates), [own_Templates]);

  const phone = phoneE164(contact.phone);
  const AR = contact.archived ? 'Archived - unarchive to contact' : '';
  const callBlock = AR || (!phone ? 'Add a phone number' : contact.dnc ? 'On Do Not Call: no calls or texts' : optedOutCalls(contact) ? 'Opted out of phone calls' : '');
  const textBlock = AR || (!canMessageLead(contact, uid, isAdmin) ? 'Shared with you: view only, no texts from Resi' : !phone ? 'Add a phone number' : contact.dnc ? 'On Do Not Call: no calls or texts' : optedOutTexts(contact) ? 'Opted out of text messages' : '');
  const emailBlock = AR || (!canMessageLead(contact, uid, isAdmin) ? 'Shared with you: view only, no email from Resi' : !contact.email ? 'Add an email address' : contact.email_opt_out ? 'Opted out of email' : '');

  const reset = () => { setMode(null); setResult(''); setNote(''); setTplId(''); setSubject(''); setBody(''); setMsg(''); };
  const [reengageFor, setReengageFor] = useState(null);
  const [callbackFor, setCallbackFor] = useState(null);
  const [apptFor, setApptFor] = useState(null);
  const [logAppt, setLogAppt] = useState(false);
  const [callLogId, setCallLogId] = useState(null);
  const log = async (entry, existingId = null) => {
    const at = new Date().toISOString();
    await updateContactChecked(contact, { lastContactedAt: at, lastContactedBy: uid || null, lastContactType: entry.type }, uid, isAdmin);
    if (existingId) await updateActivity(contact.id, existingId, entry);
    else await logActivity(contact.id, entry, uid);
    // Kelly: any call/text to the lead from Resi auto-tags #attemptedcontact.
    if (entry.type === 'call' || entry.type === 'text') await markAttemptedContact(contact);
    // Logging the call or text clears the task it was for, so it comes off the task list.
    if (entry.type === 'call' || entry.type === 'text') {
      try {
        const cleared = await clearTasksForLoggedContact({ uid, contact, type: entry.type, task });
        if (cleared.length && onTaskCleared) onTaskCleared(cleared);
      } catch (err) { console.error('Could not clear the task for this call', err); }
    }
  };

  const okIfPaused = () => { const p = pauseInfo(contact); return !p || window.confirm(`${p.label}. Contact anyway?`); };
  // tel: call: log "Call started" right away, then the popup asks for the outcome and finishes that same entry.
  const startCall = () => {
    if (!okIfPaused()) return;
    logActivity(contact.id, { type: 'call_attempt', via: 'tel', ...callFields(phone, auth.currentUser, senderName ? { agentName: senderName } : {}), text: `Call started to ${formatPhoneDash(contact.phone)}` }, uid)
      .then((id) => setCallLogId(id)).catch((err) => console.error('Could not log call start', err));
    window.location.href = `tel:${phone}`; setMode('call');
  };
  const startQuo = () => { if (!okIfPaused()) return; setBody(task && task.action === 'text' ? script : ''); setMsg(''); setMode('quo'); };
  const [sending, setSending] = useState(false);
  const sendQuo = async () => {
    const content = body.trim();
    if (!content) { setMsg('Type a message first.'); return; }
    setSending(true); setMsg('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/api/quo-send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ to: phone, content, contactId: contact.id }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg(data.error || `Could not send (error ${res.status}).`); return; }
      if (data.dryRun) { setMsg(`Test mode: nothing was sent (would go to ${data.to} from ${data.from}).`); return; }
      await log({ type: 'text', via: 'quo', notes: content, text: `Text sent via Quo: ${content}` });
      reset();
    } catch (err) { setMsg(err.message || 'Could not send the text.'); } finally { setSending(false); }
  };
  const startText = () => { if (!okIfPaused()) return; const b = task && task.action === 'text' ? script : ''; if (b) setNote(b); window.location.href = `sms:${phone}${b ? `?&body=${encodeURIComponent(b)}` : ''}`; setMode('text'); };

  const saveCall = async () => {
    try {
      await log({ type: 'call', via: 'tel', ...callFields(phone, auth.currentUser, senderName ? { agentName: senderName } : {}), disposition: result || 'Called', notes: note.trim(), text: `Call: ${result || 'Called'}${note.trim() ? `. ${note.trim()}` : ''}` }, callLogId);
      setCallLogId(null);
      if (result === 'Asked not to call') await recordAskedNotToCall(contact.id, 'Call from contact page', note.trim(), uid);
      if (isNotInterested(result)) setReengageFor(contact);
      if (result === 'Call back') setCallbackFor(contact);
      if (result === 'Appointment set') setApptFor(contact);
      reset();
    } catch (err) { setMsg(err.message || 'Could not log the call.'); }
  };
  const saveText = async () => {
    try { await log({ type: 'text', notes: note.trim(), text: `Text sent${note.trim() ? `: ${note.trim()}` : ''}` }); reset(); } catch (err) { setMsg(err.message || 'Could not log the text.'); }
  };
  const pickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) { setSubject(t.subject || ''); setBody(plainFromBlocks(t.blocks)); }
  };
  const openMail = async () => {
    const url = `mailto:${encodeURIComponent(contact.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    try {
      await log({ type: 'email', via: 'mail_app', direction: 'outbound', agentName: senderName || (auth.currentUser && (auth.currentUser.displayName || auth.currentUser.email)) || '', subject, snippet: String(body || '').replace(/\s+/g, ' ').trim().slice(0, 160), templateId: tplId || null, text: `Email: ${subject || '(no subject)'}${tplId ? ' (from template)' : ''}` });
      window.location.href = url;
      reset();
    } catch (err) { setMsg(err.message || 'Could not log the email.'); }
  };

  const ICON = { Call: '\u260E', Text: '\u{1F4AC}', 'Send via Quo': <span style={{ fontWeight: 800, fontSize: 18, fontFamily: 'Arial, sans-serif', color: '#1A1A1A' }}>Q</span>, Email: '\u2709' };
  const action = (label, onClick, reason) => (
    <span title={reason ? `${label}: ${reason}` : label} style={compact ? { display: 'inline-flex' } : { flex: '1 1 90px', display: 'inline-flex' }}>
      <button
        style={compact
          ? { ...ui.btn, padding: 0, width: 40, minHeight: 40, fontSize: 16, letterSpacing: 0, ...(reason ? { opacity: 0.35, cursor: 'not-allowed', boxShadow: 'none' } : {}) }
          : { ...ui.btn, flex: 1, ...(reason ? { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' } : {}) }}
        disabled={!!reason} onClick={onClick} aria-label={reason ? `${label} unavailable: ${reason}` : label}>
        {compact ? ICON[label] : label}
      </button>
    </span>
  );

  return (
    // Clicks here (including inside the modal) must not reach a clickable contact row behind it.
    <span onClick={(e) => e.stopPropagation()} style={compact ? { display: 'inline-flex' } : { display: 'block' }}>
      <ReengageModal contact={reengageFor} uid={uid} isAdmin={isAdmin} onDone={() => setReengageFor(null)} />
      <CallbackModal contact={callbackFor} uid={uid} isAdmin={isAdmin} onDone={() => setCallbackFor(null)} />
      <AppointmentModal contact={apptFor} uid={uid} isAdmin={isAdmin} onDone={() => setApptFor(null)} />
      <div style={compact ? { display: 'flex', gap: 6 } : { ...ui.row, margin: '12px 0 4px' }}>
        {action('Call', startCall, callBlock)}
        {action('Text', startText, textBlock)}
        {QUO_UI_EMAILS.includes(String(auth.currentUser?.email || '').toLowerCase()) && action('Send via Quo', startQuo, textBlock)}
        {action('Email', () => setMode('email'), emailBlock)}
        {!compact && action('Log appt', () => setLogAppt(true), '')}
      </div>
      {logAppt && <LogAppointmentModal uid={uid} contact={contact} onDone={(ok) => { setLogAppt(false); if (ok) window.alert('Appointment logged in My Outreach.'); }} />}
      {!compact && (!phone || !contact.email) && (
        <div style={{ fontSize: 12, color: MUTED, margin: compact ? '2px 0 0' : '0 0 4px' }}>
          {!phone && !contact.email ? 'Add a phone number and email (Edit) to call, text, or email.' : !phone ? 'Add a phone number (Edit) to call or text.' : 'Add an email address (Edit) to send email.'}
        </div>
      )}
      {!compact && contact.lastContactedAt && <div style={{ fontSize: 12, color: MUTED }}>Last contacted {new Date(contact.lastContactedAt).toLocaleString()}{contact.lastContactType ? ` (${contact.lastContactType})` : ''}</div>}

      {mode && (
        <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) reset(); }}>
          <div style={modal} role="dialog" aria-modal="true">
            {mode === 'call' && (<>
              <h3 style={ui.sectionTitle}>Log call with {contact.firstName || contact.name}</h3>
              <p style={ui.sub}>Calling {formatPhoneDash(contact.phone)}. On a computer, dial from your phone, then log the result here.</p>
              {script && task.action === 'call' && (
                <div data-testid="call-script" style={{ border: '2px solid #3aa0dc', borderRadius: 8, padding: 12, margin: '8px 0 12px', background: '#F3F9FD' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Call script: {task.name ? String(task.name).split(':')[0] : 'this step'}</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.45 }}>{script}</div>
                </div>
              )}
              <label style={ui.label}>Result</label>
              <select style={ui.input} value={result} onChange={(e) => setResult(e.target.value)}>
                <option value="">Called</option>
                <option value="Connected">Connected</option>
                {DISPOSITIONS.map((d) => <option key={d} value={d}>{d === 'Left VM' ? 'Left voicemail' : d}</option>)}
              </select>
              <label style={ui.label}>Notes</label>
              <textarea style={{ ...ui.input, minHeight: 70 }} value={note} onChange={(e) => setNote(e.target.value)} />
              {noCalls(contact) && <p style={{ color: DANGER, fontSize: 13 }}>This contact can't be called.</p>}
            </>)}
            {mode === 'text' && (<>
              <h3 style={ui.sectionTitle}>Log text to {contact.firstName || contact.name}</h3>
              <p style={ui.sub}>Your messages app opened for {formatPhoneDash(contact.phone)}. Add a quick note of what you sent.</p>
              {script && task.action === 'text' && <button style={{ ...ui.ghost, marginBottom: 8 }} onClick={() => navigator.clipboard && navigator.clipboard.writeText(script)}>Copy text script</button>}
              <label style={ui.label}>What did you send?</label>
              <textarea style={{ ...ui.input, minHeight: 70 }} value={note} onChange={(e) => setNote(e.target.value)} />
            </>)}
            {mode === 'quo' && (<>
              <h3 style={ui.sectionTitle}>Text {contact.firstName || contact.name} via Quo</h3>
              <p style={ui.sub}>Sends from your Quo business number to {formatPhoneDash(contact.phone)}. It's logged on this contact's timeline.</p>
              <label style={ui.label}>To</label>
              <input style={ui.input} value={formatPhoneDash(contact.phone)} readOnly />
              <label style={ui.label}>Message</label>
              <textarea style={{ ...ui.input, minHeight: 110 }} value={body} maxLength={1600} onChange={(e) => setBody(e.target.value)} placeholder={`Hi ${contact.firstName || ''}, `} />
            </>)}
            {mode === 'email' && (<>
              <h3 style={ui.sectionTitle}>Email {contact.firstName || contact.name}</h3>
              <label style={ui.label}>Start from a template (optional)</label>
              <select style={ui.input} value={tplId} onChange={(e) => pickTemplate(e.target.value)}>
                <option value="">Simple message</option>
                {visibleTemplates(templates, uid).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <label style={ui.label}>Subject</label>
              <input style={ui.input} value={subject} onChange={(e) => setSubject(e.target.value)} />
              <label style={ui.label}>Message</label>
              <textarea style={{ ...ui.input, minHeight: 140 }} value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Hi ${contact.firstName || ''},\n\n\n${senderName || ''}`} />
              {!NOTIFICATIONS_ENABLED && <div style={{ ...ui.notice, marginTop: 10 }}>Sending from Resi isn't turned on yet. "Open in my email" opens your own mail app with this message filled in (plain text; the designed template sends once email is connected).</div>}
            </>)}
            {msg && <p style={{ color: DANGER, fontSize: 13 }}>{msg}</p>}
            <div style={{ ...ui.row, marginTop: 14 }}>
              {mode === 'call' && <button style={ui.btn} onClick={saveCall}>Save call</button>}
              {mode === 'text' && <button style={ui.btn} onClick={saveText}>Log text</button>}
              {mode === 'quo' && <button style={ui.btn} onClick={sendQuo} disabled={sending}>{sending ? 'Sending...' : 'Send'}</button>}
              {mode === 'email' && <button style={ui.btn} onClick={openMail}>Open in my email</button>}
              {mode === 'email' && <button style={{ ...ui.ghost, opacity: 0.5, cursor: 'not-allowed' }} disabled title="Coming when email sending is connected">Send from Resi</button>}
              <button style={ui.ghost} onClick={reset}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
